import { DotIcon, InfoIcon, Loader2Icon } from 'lucide-react';
import {
  Button,
  ModalContent,
  useErrorHandler,
  DatePicker,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@sandtech-hub-monorepo/ui-kit';
import React, { useState } from 'react';
import {
  formatDate,
  parseCalendarDate,
  toCalendarDate,
} from '../../dateFormat';
import {
  ApplyToProgramAndUpdateUserDocument,
  ApplyToProgramDocument,
  UpdateUserDocument,
  Gender,
} from '@sandtech-hub-monorepo/ehub-client';
import { useNavigate } from 'react-router-dom';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CountryPicker } from '../../geo-picker/CountryPicker';
import { useMutation } from '@apollo/client';
import { PhoneNumberFields } from './PhoneNumberFields';
import { pushDataLayerEvent } from '../analytics';

const formSchema = z.object({
  phoneNumber: z.string().min(5, 'Please enter a valid phone number'),
  whatsAppNumber: z.string().min(5, 'Please enter a valid WhatsApp number'),
  sameAsPrimaryPhone: z.boolean(),
  birthDate: z.date({ required_error: 'Please select your date of birth' }),
  countryOfResidence: z
    .string()
    .min(1, 'Please select your country of residence'),
  gender: z.nativeEnum(Gender, { required_error: 'Please select your gender' }),
});

interface Props {
  id: string;
  name: string;
  abbreviation?: string | null;
  duration?: string;
  commitment: string;
  startDate?: Date;
  requiresPreferredHubForEnrollment: boolean;
  preferredHubOption: { displayName: string; value: string }[];
  learningPageLink: string;
  isTestVariant?: boolean;
  onNext?: () => void;
  allInfoCompleted?: boolean;
}

export const ProgramEnrollmentConfirmModal = ({
  id,
  name,
  abbreviation,
  duration,
  commitment,
  startDate,
  requiresPreferredHubForEnrollment,
  preferredHubOption,
  learningPageLink,
  isTestVariant = false,
  onNext,
  allInfoCompleted = false,
}: Props) => {
  const { handleApolloError } = useErrorHandler();
  const navigate = useNavigate();
  const [enroll, { loading: enrollLoading }] = useMutation(
    ApplyToProgramDocument,
    {
      onError: handleApolloError,
      onCompleted: (data) => {
        const enrollmentId =
          data?.applyToProgram?.user?.programEnrollments?.find(
            (pe) => pe.program?.id === id
          )?.id;
        const isFirstEnrollment =
          data?.applyToProgram?.user?.programEnrollments?.length === 1;
        pushDataLayerEvent({
          event: 'program_enrolled',
          program_id: id,
          program_name: name,
          program_abbreviation: abbreviation,
          program_enrollment_id: enrollmentId,
          user_id: viewer?.sandId,
        });
        if (isFirstEnrollment) {
          pushDataLayerEvent({
            event: 'first_program_enrolled',
            program_id: id,
            program_name: name,
            program_abbreviation: abbreviation,
            program_enrollment_id: enrollmentId,
            user_id: viewer?.sandId,
          });
        }
        navigate(learningPageLink);
      },
    }
  );
  const [enrollAndUpdateUser, { loading: enrollAndUpdateLoading }] =
    useMutation(ApplyToProgramAndUpdateUserDocument, {
      onError: handleApolloError,
      onCompleted: (data) => {
        const enrollmentId =
          data?.applyToProgram?.user?.programEnrollments?.find(
            (pe) => pe.program?.id === id
          )?.id;
        const isFirstEnrollment =
          data?.applyToProgram?.user?.programEnrollments?.length === 1;
        pushDataLayerEvent({
          event: 'program_enrolled',
          program_id: id,
          program_name: name,
          program_abbreviation: abbreviation,
          program_enrollment_id: enrollmentId,
          user_id: viewer?.sandId,
        });
        if (isFirstEnrollment) {
          pushDataLayerEvent({
            event: 'first_program_enrolled',
            program_id: id,
            program_name: name,
            program_abbreviation: abbreviation,
            program_enrollment_id: enrollmentId,
            user_id: viewer?.sandId,
          });
        }
        navigate(learningPageLink);
      },
    });
  const [updateUser, { loading: updatingUser }] = useMutation(
    UpdateUserDocument,
    {
      onError: handleApolloError,
    }
  );
  const [preferredHub, setPreferredHub] = useState('');

  const { viewer } = useGetMaybeViewer();

  const isFieldMissing = {
    phoneNumber: !viewer?.phoneNumber || viewer.phoneNumber.length < 5,
    whatsAppNumber: !viewer?.whatsAppNumber || viewer.whatsAppNumber.length < 5,
    birthDate: !viewer?.birthDate,
    countryOfResidence: !viewer?.countryOfResidence,
    gender: !viewer?.gender,
  };

  const needsUserUpdate = Object.values(isFieldMissing).some(Boolean);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: {
      phoneNumber: viewer?.phoneNumber || '',
      whatsAppNumber: viewer?.whatsAppNumber || '',
      sameAsPrimaryPhone: viewer?.phoneNumber === viewer?.whatsAppNumber,
      birthDate: parseCalendarDate(viewer?.birthDate) ?? undefined,
      countryOfResidence: viewer?.countryOfResidence || undefined,
      gender: viewer?.gender || undefined,
    },
  });

  const submit = async () => {
    const { sameAsPrimaryPhone, birthDate, ...rest } = form.getValues();
    // The picker hands back local midnight, so this sends the day the learner
    // clicked. Passing the Date straight through would have Apollo serialize
    // the UTC instant, shifting the date for anyone east of Greenwich.
    const userData = { ...rest, birthDate: toCalendarDate(birthDate) };

    if (isTestVariant && onNext) {
      // For test variant: just update user data if needed, then proceed to next modal
      // DO NOT create enrollment yet - PersonalInformationModal will do that
      if (needsUserUpdate) {
        await updateUser({
          variables: { input: userData },
        });
      }
      onNext();
    } else {
      // Normal flow: create enrollment immediately
      const programInput = {
        programId: id,
        ...(preferredHub && { preferredHub }),
      };

      if (needsUserUpdate) {
        enrollAndUpdateUser({
          variables: {
            programInput,
            updateUserInput: userData,
          },
        });
      } else {
        enroll({
          variables: { programInput },
        });
      }
    }
  };

  return (
    <ModalContent className="max-w-md">
      <div className="flex flex-col items-center px-4">
        <div className="bg-primary/20 p-4 inline rounded-full">
          <InfoIcon
            width={32}
            height={32}
            className="text-primary inline-block"
          />
        </div>
        <h2 className="text-primary font-semibold text-xl mt-3">
          Confirm enrolment
        </h2>
        <div className="text-sm text-muted-foreground">
          You're about to enrol in:
        </div>
        <h3 className="text-primary font-semibold text-lg mt-2">{name}</h3>
        <div className="text-sm text-center text-muted-foreground mt-1">
          Duration: {duration || '-'}
          <DotIcon className="inline w-3 h-3 mx-1" />
          Commitment: {commitment}
          <DotIcon className="inline w-3 h-3 mx-1" />
          {startDate && (
            <>
              <DotIcon className="inline w-3 h-3 mx-1" />
              Start date:{' '}
              {startDate && startDate.getTime() > Date.now()
                ? formatDate(startDate)
                : 'Any time'}
            </>
          )}
        </div>
        <Form {...form}>
          {(needsUserUpdate || requiresPreferredHubForEnrollment) && (
            <div className="flex flex-col gap-3 mt-4 w-full max-w-sm">
              {isFieldMissing.birthDate && (
                <FormField
                  control={form.control}
                  name="birthDate"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="text-sm">Date of birth*</FormLabel>
                      <FormControl>
                        <DatePicker
                          selected={field.value}
                          onSelect={field.onChange}
                          disabledDates={(date: Date) =>
                            date.valueOf() > Date.now()
                          }
                          triggerButtonVariant="outlineFg"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              )}
              {isFieldMissing.countryOfResidence && (
                <FormField
                  control={form.control}
                  name="countryOfResidence"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel htmlFor="country" className="text-sm">
                        Country of residence*
                      </FormLabel>
                      <FormControl>
                        <CountryPicker
                          handleOnSelect={({ value }) => field.onChange(value)}
                          selected={field.value}
                          triggerVariant="outlineFg"
                          triggerClassName="rounded-lg font-normal"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              )}
              {isFieldMissing.gender && (
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel htmlFor="gender" className="text-sm">
                        Gender*
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={Gender.Male}>Male</SelectItem>
                            <SelectItem value={Gender.Female}>
                              Female
                            </SelectItem>
                            <SelectItem value={Gender.Other}>Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              )}
              {(isFieldMissing.phoneNumber ||
                isFieldMissing.whatsAppNumber) && (
                <PhoneNumberFields
                  control={form.control}
                  watch={form.watch}
                  setValue={form.setValue}
                />
              )}
              {requiresPreferredHubForEnrollment && (
                <Select
                  value={preferredHub}
                  onValueChange={setPreferredHub}
                  defaultValue="Select Preferred Hub"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Preferred Hub" />
                  </SelectTrigger>
                  <SelectContent>
                    {preferredHubOption.map(({ value, displayName }) => (
                      <SelectItem value={value} key={value}>
                        {displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </Form>
        <Button
          disabled={
            enrollLoading ||
            enrollAndUpdateLoading ||
            updatingUser ||
            !form.formState.isValid ||
            (requiresPreferredHubForEnrollment && !preferredHub)
          }
          className="mt-4 mb-4"
          onClick={submit}
        >
          {enrollLoading || enrollAndUpdateLoading || updatingUser ? (
            <>
              {isTestVariant && !allInfoCompleted
                ? 'Saving...'
                : 'Enrolling...'}{' '}
              <Loader2Icon className="animate-spin ml-2 w-4 h-4" />
            </>
          ) : isTestVariant && !allInfoCompleted ? (
            'Next'
          ) : (
            'Confirm enrolment'
          )}
        </Button>
      </div>
    </ModalContent>
  );
};
